import PropTypes from 'prop-types';
import usePermission from '../hooks/usePermission';

/**
 * PermissionGate
 *
 * Renders `children` only when the current user has the required permission.
 * Per AC7 of the RBAC user story: "Restricted UI elements are hidden in frontend & mobile."
 *
 * Usage:
 *   <PermissionGate module="pbl" action="create">
 *     <Button>Add New</Button>
 *   </PermissionGate>
 *
 *   // With a fallback element:
 *   <PermissionGate module="courses" action="delete" fallback={<Tooltip title="No permission"><span><Button disabled>Delete</Button></span></Tooltip>}>
 *     <Button onClick={handleDelete}>Delete</Button>
 *   </PermissionGate>
 *
 * Props:
 *   module   {string}      - Permission module, e.g. "pbl", "courses"
 *   action   {string}      - Permission action, e.g. "create", "delete", "export"
 *   fallback {ReactNode}   - Optional element to render when permission is denied (default: null = hidden)
 *   children {ReactNode}   - Content to show when permission is granted
 */
export default function PermissionGate({ module, action, fallback = null, children }) {
  const { can } = usePermission();

  if (!can(module, action)) {
    return fallback;
  }

  return children;
}

PermissionGate.propTypes = {
  module: PropTypes.string.isRequired,
  action: PropTypes.string.isRequired,
  fallback: PropTypes.node,
  children: PropTypes.node.isRequired,
};
